import * as React from "react";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";

import { ITableProps } from "src/library/components/Tables/Table";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { Text } from "src/library/components/Text";
import { IShutdown } from "src/modules/shutdowns/api/shutdown.api";
import { formatDropName } from "src/core/drops/utils/format-drop-name";
import { formatCustomerName } from "src/core/customers/utils/format-customer-name";

export interface IAllBranchesShutdownsColumns extends Partial<ITableProps> {
  actionButton: (info: CellContext<IShutdown, unknown>) => React.ReactNode;
  terminology: ITerminology;
  // Enables the ability to request specific columns as apposed to all
  selectedColumns?: AllBranchesShutdownsTableColumn[];
}

export type AllBranchesShutdownsTableColumn = keyof AllBranchesShutdownsColumns;

// used to determine what columns can be rendered
interface AllBranchesShutdownsColumns {
  customer: ColumnDef<IShutdown>;
  dropNumber: ColumnDef<IShutdown, unknown>;
  reason: ColumnDef<IShutdown, unknown>;
  startDate: ColumnDef<IShutdown, unknown>;
  endDate: ColumnDef<IShutdown, unknown>;
  actions: ColumnDef<IShutdown, unknown>;
}

export const getAllBranchesShutdownsColumns = (params: IAllBranchesShutdownsColumns): Array<ColumnDef<IShutdown, unknown>> => {
  const customColumnHelper = customCreateColumnHelper<IShutdown>();
  const columnHelper = createColumnHelper<IShutdown>();
  let columns: Array<ColumnDef<IShutdown, unknown>>;

  const availableColumns: AllBranchesShutdownsColumns = {
    customer: customColumnHelper.standardTextColumn(columnHelper, "branch.customer", {
      header: "Customer",
      enableSorting: false,
      meta: {
        filter: {
          filterType: "string",
          filterKeyOverride: "customer"
        }
      },
      cell: info => {
        return (
          <Text>
            {info.row.original.branch?.customer ? formatCustomerName(info.row.original.branch?.customer) : "-"}
          </Text>
        );
      }
    }),
    dropNumber: customColumnHelper.standardTextColumn(columnHelper, "branch", {
      header: "Drop",
      enableSorting: false,
      cell: info => {
        return (
          <Text>
            {info.row.original.branch ? formatDropName(info.row.original.branch) : "-"}
          </Text>
        );
      }
    }),
    startDate: customColumnHelper.dateColumn(columnHelper, "startDate", { header: "Start Date" }),
    endDate: customColumnHelper.dateColumn(columnHelper, "endDate", { header: "End Date" }),
    reason: customColumnHelper.standardTextColumn(columnHelper, "reason", {
      header: "Reason",
      enableSorting: false
    }),
    actions: customColumnHelper.actionColumn(columnHelper, params.actionButton, {})
  };

  // only return selected columns if requested
  if (params.selectedColumns) {
    return params.selectedColumns.map(requestedColumn => availableColumns[ requestedColumn ]);
  } else {
  // otherwise return all by default
    columns = Object.values(availableColumns);
  }

  return columns;
};
